import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Search, X } from "lucide-react";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");

  const cat = params.get("cat") || "";
  const sort = params.get("sort") || "default";

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {}); }, []);

  // Debounce the search term into the URL so the effect below refetches
  // as the user types (250ms feels instant without hammering the API).
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (q) next.set("q", q); else next.delete("q");
      setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const activeQ = params.get("q") || "";

  useEffect(() => {
    setLoading(true);
    const args = {};
    if (cat) args.category_slug = cat;
    if (activeQ) args.q = activeQ;
    api.get("/products", { params: args }).then((r) => {
      let data = r.data || [];
      if (sort === "low") data = [...data].sort((a, b) => (a.price_usd || 0) - (b.price_usd || 0));
      if (sort === "high") data = [...data].sort((a, b) => (b.price_usd || 0) - (a.price_usd || 0));
      if (sort === "sale") data = [...data].sort((a, b) => Number(b.on_sale) - Number(a.on_sale));
      setItems(data); setLoading(false);
    }).catch(() => setLoading(false));
  }, [cat, activeQ, sort]);

  // Refetch when browser restores this page from BFCache (browser back button)
  // or when the tab regains focus — otherwise admin price edits look "stuck".
  useEffect(() => {
    const refetch = () => {
      const args = {};
      if (cat) args.category_slug = cat;
      if (activeQ) args.q = activeQ;
      api.get("/products", { params: args }).then((r) => setItems(r.data || [])).catch(() => {});
    };
    const onPageShow = (e) => { if (e.persisted) refetch(); };
    const onFocus = () => refetch();
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onFocus);
    };
  }, [cat, activeQ]);

  const setCat = (slug) => {
    const next = new URLSearchParams(params);
    if (slug) next.set("cat", slug); else next.delete("cat");
    setParams(next);
  };
  const setSort = (s) => {
    const next = new URLSearchParams(params);
    next.set("sort", s);
    setParams(next);
  };

  return (
    <div>
      {/* Header */}
      <section className="bg-[var(--paper-2)] border-b border-[var(--line)]">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-12">
          <div className="eyebrow">Catalog</div>
          <h1 className="h-display text-4xl md:text-6xl mt-2">{cat ? cats.find((c) => c.slug === cat)?.name || "Shop" : "Shop the catalog."}</h1>
          <p className="mt-3 max-w-2xl text-[var(--ink-soft)]">Reference compounds, metabolic research peptides, and laboratory consumables — supplied to accredited labs and qualified researchers. Every batch published with a Koveralabs-verified COA.</p>

          {/* Prominent live-search bar — always visible, updates as you type. */}
          <div className="mt-8 relative max-w-2xl" data-testid="shop-search-hero-wrap">
            <Search className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search compounds by name, SKU, or CAS number…"
              className="w-full pl-12 pr-12 py-3.5 bg-[var(--paper)] text-[var(--ink)] rounded-full border border-[var(--line)] focus:border-[var(--terra)] outline-none text-[15px] placeholder:text-[var(--ink-muted)]"
              data-testid="shop-search"
              autoComplete="off"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-[var(--paper-2)]"
                aria-label="Clear search"
                data-testid="shop-search-clear"
              >
                <X className="h-4 w-4 text-[var(--ink-muted)]" />
              </button>
            )}
          </div>
          {activeQ && (
            <div className="mt-3 text-[12px] font-mono text-[var(--ink-muted)]" data-testid="shop-search-status">
              {loading ? "Searching…" : `${items.length} result${items.length === 1 ? "" : "s"} for "${activeQ}"`}
            </div>
          )}
        </div>
      </section>

      {/* Filter bar */}
      <section className="border-b border-[var(--line)] bg-[var(--paper-2)] sticky top-20 z-30">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-4 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
          <div className="flex flex-wrap gap-2" data-testid="cat-filters">
            <button onClick={() => setCat("")} className={`btn-pill ${!cat ? "active" : ""}`} data-testid="cat-all">All ({items.length})</button>
            {cats.map((c) => (
              <button key={c.slug} onClick={() => setCat(c.slug)} className={`btn-pill ${cat === c.slug ? "active" : ""}`} data-testid={`cat-${c.slug}`}>{c.name} ({c.count})</button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-[var(--line)] bg-[var(--paper-2)] text-sm rounded-full px-4 py-2 font-mono uppercase tracking-wider text-[12px]" data-testid="sort-select">
              <option value="default">Sort by</option>
              <option value="low">Price · Low to High</option>
              <option value="high">Price · High to Low</option>
              <option value="sale">On Sale First</option>
            </select>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-10">
        {loading ? (
          <div className="text-[var(--ink-muted)] font-mono text-xs uppercase tracking-[0.2em]">Loading catalog…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <div className="font-display text-2xl">No matches.</div>
            <div className="text-sm text-[var(--ink-muted)] mt-2">Try clearing filters or searching another term.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {items.map((p) => <ProductCard key={p.sku} p={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
